/**
 * Traducción al español de los mensajes de error que devuelve MetaAPI.
 *
 * MetaAPI devuelve errores en dos formas distintas:
 * - Rechazos de trading: un `stringCode` fijo del enum ENUM_TRADE_RETCODE de MetaTrader
 *   (ej. TRADE_RETCODE_INVALID_VOLUME) acompañado de un `message` en inglés.
 * - Errores HTTP genéricos: una clase de error fija (ej. ValidationError, NotFoundError)
 *   acompañada de un `message` de texto libre.
 *
 * Para códigos/clases no reconocidos se conserva el mensaje original de MetaAPI como
 * respaldo, para no ocultar información al usuario.
 */

const TRADE_RETCODE_MESSAGES: Record<string, string> = {
  TRADE_RETCODE_REQUOTE: 'Nueva cotización recibida (requote)',
  TRADE_RETCODE_REJECT: 'Solicitud rechazada por el bróker',
  TRADE_RETCODE_CANCEL: 'Solicitud cancelada por el trader',
  TRADE_RETCODE_PLACED: 'Orden colocada',
  TRADE_RETCODE_DONE: 'Solicitud completada',
  TRADE_RETCODE_DONE_PARTIAL: 'Solicitud completada parcialmente',
  TRADE_RETCODE_ERROR: 'Error al procesar la solicitud',
  TRADE_RETCODE_TIMEOUT: 'Solicitud cancelada por tiempo de espera agotado',
  TRADE_RETCODE_INVALID: 'Solicitud inválida',
  TRADE_RETCODE_INVALID_VOLUME: 'Volumen inválido en la solicitud',
  TRADE_RETCODE_INVALID_PRICE: 'Precio inválido en la solicitud',
  TRADE_RETCODE_INVALID_STOPS: 'Stop Loss / Take Profit inválidos en la solicitud',
  TRADE_RETCODE_TRADE_DISABLED: 'El trading está deshabilitado en esta cuenta',
  TRADE_RETCODE_MARKET_CLOSED: 'El mercado está cerrado',
  TRADE_RETCODE_NO_MONEY: 'Fondos insuficientes para completar la solicitud',
  TRADE_RETCODE_PRICE_CHANGED: 'El precio cambió, intenta de nuevo',
  TRADE_RETCODE_PRICE_OFF: 'No hay cotizaciones disponibles para procesar la solicitud',
  TRADE_RETCODE_INVALID_EXPIRATION: 'Fecha de expiración inválida en la solicitud',
  TRADE_RETCODE_ORDER_CHANGED: 'El estado de la orden cambió',
  TRADE_RETCODE_TOO_MANY_REQUESTS: 'Demasiadas solicitudes, intenta de nuevo en unos segundos',
  TRADE_RETCODE_NO_CHANGES: 'No hay cambios en la solicitud',
  TRADE_RETCODE_SERVER_DISABLES_AT: 'El servidor deshabilitó el trading automático',
  TRADE_RETCODE_CLIENT_DISABLES_AT: 'Algo Trading está desactivado en MetaTrader 5: actívalo en el terminal para enviar órdenes',
  TRADE_RETCODE_LOCKED: 'La solicitud está bloqueada para su procesamiento',
  TRADE_RETCODE_FROZEN: 'La orden o posición está congelada',
  TRADE_RETCODE_INVALID_FILL: 'Tipo de ejecución (fill) inválido en la solicitud',
  TRADE_RETCODE_CONNECTION: 'Sin conexión con el servidor de trading',
  TRADE_RETCODE_ONLY_REAL: 'Esta operación solo está permitida en cuentas reales',
  TRADE_RETCODE_LIMIT_ORDERS: 'Se alcanzó el límite de órdenes pendientes',
  TRADE_RETCODE_LIMIT_VOLUME: 'Se alcanzó el límite de volumen de órdenes',
  TRADE_RETCODE_INVALID_ORDER: 'Tipo de orden inválido',
  TRADE_RETCODE_POSITION_CLOSED: 'La posición ya está cerrada',
  TRADE_RETCODE_INVALID_CLOSE_VOLUME: 'Volumen de cierre inválido',
  TRADE_RETCODE_CLOSE_ORDER_EXIST: 'Ya existe una orden de cierre para esta posición',
  TRADE_RETCODE_LIMIT_POSITIONS: 'Se alcanzó el límite de posiciones abiertas',
  TRADE_RETCODE_REJECT_CANCEL: 'Se rechazó la cancelación de la orden pendiente',
  TRADE_RETCODE_LONG_ONLY: 'Esta cuenta solo permite posiciones largas (compra)',
  TRADE_RETCODE_SHORT_ONLY: 'Esta cuenta solo permite posiciones cortas (venta)',
  TRADE_RETCODE_CLOSE_ONLY: 'Esta cuenta solo permite cerrar posiciones existentes',
  TRADE_RETCODE_FIFO_CLOSE: 'El cierre debe respetar la regla FIFO',
  TRADE_RETCODE_HEDGE_PROHIBITED: 'La cobertura (hedging) está prohibida en esta cuenta',
};

const META_API_ERROR_CLASS_MESSAGES: Record<string, string> = {
  ValidationError: 'La solicitud enviada a MetaAPI es inválida',
  UnauthorizedError: 'Sesión de MetaAPI inválida o expirada, inicia sesión nuevamente',
  ForbiddenError: 'No tienes permiso para realizar esta operación en MetaAPI',
  NotFoundError: 'No se encontró la cuenta o el recurso solicitado en MetaAPI',
  ConflictError: 'La solicitud entra en conflicto con el estado actual de la cuenta',
  TooManyRequestsError: 'Se superó el límite de solicitudes a MetaAPI, intenta de nuevo en unos minutos',
  TimeoutError: 'MetaAPI tardó demasiado en responder, intenta de nuevo',
  InternalError: 'Error interno de MetaAPI, intenta de nuevo más tarde',
};

export function translateMetaApiError(stringCode?: string, fallbackMessage?: string): string {
  if (stringCode && TRADE_RETCODE_MESSAGES[stringCode]) {
    return TRADE_RETCODE_MESSAGES[stringCode];
  }
  return fallbackMessage ?? 'Orden rechazada por el bróker';
}

export function translateMetaApiHttpError(errorClass?: string, fallbackMessage?: string): string {
  if (errorClass && META_API_ERROR_CLASS_MESSAGES[errorClass]) {
    return META_API_ERROR_CLASS_MESSAGES[errorClass];
  }
  return fallbackMessage ?? 'Error desconocido al comunicarse con MetaAPI';
}

/** Retcodes of a /trade request that went through (pending order placed, or done in full or in part). */
const TRADE_RETCODES_OK = new Set(['TRADE_RETCODE_DONE', 'TRADE_RETCODE_PLACED', 'TRADE_RETCODE_DONE_PARTIAL']);

/**
 * Why a /trade request (placeOrder/modifyOrder/cancelOrder) was not executed, or null if it was.
 * Those methods never throw: HTTP failures come back as { error } and MT5 rejections with a
 * stringCode that isn't a success.
 */
export function motivoRechazoMetaApi(res: any): string | null {
  if (res?.error) {
    const httpErr = res.error;
    return translateMetaApiHttpError(httpErr?.error?.error, httpErr?.error?.message ?? httpErr?.message);
  }
  if (res?.stringCode && !TRADE_RETCODES_OK.has(res.stringCode)) {
    return translateMetaApiError(res.stringCode, res.message);
  }
  return null;
}
